<script setup lang="ts">
  defineOptions({ name: 'EventDetailAttendeesSection' })

  import { computed, ref, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Mail01Icon, MessageChatCircleIcon, UserPlus01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPTag from '@ui/components/CPTag.vue'

  import CPText from '@ui/components/CPText.vue'

  import EventDetailTabPanel from './EventDetailTabPanel.vue'

  import { useDirectoryPagination } from '../composables/useDirectoryPagination'

  import type { EventDetailAttendee, EventDetailTagColor } from '../types/event-detail'

  const props = defineProps({
    attendees: {
      type: Array as PropType<readonly EventDetailAttendee[]>,
      required: true,
    },
    attendeesTotalCount: {
      type: Number,
      default: undefined,
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    chat: [attendeeId: string]
  }>()

  const BADGE_COLORS: Record<string, EventDetailTagColor> = {
    VIP: 'warning',
    Speaker: 'blue-light',
    Member: 'gray',
    Guest: 'gray',
    Following: 'purple',
  }

  const PAGE_SIZE = 12

  const { t } = useI18n()

  const searchQuery = ref('')

  const activeFilter = ref<AttendeeFilterId>('all')

  const followedIds = ref<Set<string>>(new Set())

  const formattedTotal = computed(() => (props.attendeesTotalCount ?? props.attendees.length).toLocaleString('en-US'))

  const hasFilterTags = computed(() => props.attendees.some(attendee => Boolean(attendee.badge)))

  const filterChips = computed(() => [
    { id: 'all' as const, label: t('clientportal.events.detail.attendeesFilterAll', { count: formattedTotal.value }) },
    { id: 'speakers' as const, label: t('clientportal.events.detail.attendeesFilterSpeakers') },
    { id: 'vip' as const, label: t('clientportal.events.detail.attendeesFilterVip') },
  ])

  const matchedAttendees = computed(() => {
    let list = [...props.attendees]
    if (activeFilter.value === 'speakers') {
      list = list.filter(attendee => attendee.badge === 'Speaker')
    } else if (activeFilter.value === 'vip') {
      list = list.filter(attendee => attendee.badge === 'VIP')
    }

    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return list
    return list.filter(
      attendee =>
        attendee.name.toLowerCase().includes(query) ||
        attendee.email.toLowerCase().includes(query) ||
        (attendee.role ?? '').toLowerCase().includes(query)
    )
  })

  const { pageItems, total, rangeStart, rangeEnd, canGoPrevious, canGoNext, goPrevious, goNext } = useDirectoryPagination(
    matchedAttendees,
    PAGE_SIZE
  )

  const footerLabel = computed(() =>
    t('clientportal.events.detail.pageRange', {
      start: rangeStart.value,
      end: rangeEnd.value,
      total: props.attendeesTotalCount ?? total.value,
    })
  )

  const badgeColor = (badge?: string): EventDetailTagColor => {
    if (!badge) return 'gray'
    return BADGE_COLORS[badge] ?? 'gray'
  }

  const isFilterActive = (filterId: AttendeeFilterId) => activeFilter.value === filterId

  const selectFilter = (filterId: AttendeeFilterId) => {
    activeFilter.value = filterId
  }

  const canShowChat = (attendee: EventDetailAttendee) => attendee.isSelf !== true && Boolean(attendee.contactId?.trim())

  const canShowFollow = (attendee: EventDetailAttendee) => attendee.isSelf !== true && Boolean(attendee.portalUserId)

  const isFollowing = (attendeeId: string) => followedIds.value.has(attendeeId)

  const toggleFollow = (attendeeId: string) => {
    const next = new Set(followedIds.value)
    if (next.has(attendeeId)) next.delete(attendeeId)
    else next.add(attendeeId)
    followedIds.value = next
  }

  const onChat = (attendee: EventDetailAttendee) => {
    if (!canShowChat(attendee)) return
    emit('chat', attendee.id)
  }

  const displayName = (attendee: EventDetailAttendee) =>
    attendee.isSelf === true ? t('clientportal.events.detail.attendeesYou') : attendee.name

  type AttendeeFilterId = 'all' | 'speakers' | 'vip'
</script>

<template>
  <div :id="createElementId(idPrefix, 'attendees', 'root')" class="mx-auto w-full max-w-[82.5rem] px-4 py-6 md:px-6">
    <EventDetailTabPanel
      :id-prefix="`${idPrefix}-attendees`"
      :title="t('clientportal.events.detail.attendeesTitle')"
      :search-placeholder="t('clientportal.events.detail.attendeesSearch')"
      :search-query="searchQuery"
      :footer-label="footerLabel"
      :can-go-previous="canGoPrevious"
      :can-go-next="canGoNext"
      :previous-label="t('clientportal.events.detail.previous')"
      :next-label="t('clientportal.events.detail.next')"
      @update:search-query="searchQuery = $event"
      @previous="goPrevious"
      @next="goNext"
    >
      <template v-if="hasFilterTags" #filters>
        <div :id="createElementId(idPrefix, 'attendees', 'filters')" class="flex gap-2 overflow-x-auto px-4 py-3 md:px-6">
          <CPButton
            v-for="chip in filterChips"
            :id="createElementId(idPrefix, 'attendees-chip', chip.id)"
            :key="chip.id"
            type="secondary"
            :color="isFilterActive(chip.id) ? 'custom' : 'secondary'"
            size="xs"
            class="event-detail-chip-button shrink-0"
            :custom-background-color="isFilterActive(chip.id) ? 'var(--cp-primary-50)' : undefined"
            :custom-text-color="isFilterActive(chip.id) ? 'var(--cp-primary-700)' : undefined"
            :custom-border-color="isFilterActive(chip.id) ? 'var(--cp-primary-200)' : undefined"
            :custom-hover-background-color="isFilterActive(chip.id) ? 'var(--cp-primary-100)' : undefined"
            :custom-hover-text-color="isFilterActive(chip.id) ? 'var(--cp-primary-700)' : undefined"
            :custom-hover-border-color="isFilterActive(chip.id) ? 'var(--cp-primary-200)' : undefined"
            @click="selectFilter(chip.id)"
          >
            {{ chip.label }}
          </CPButton>
        </div>
      </template>

      <div>
        <template v-for="(attendee, index) in pageItems" :key="attendee.id">
          <div :id="createElementId(idPrefix, 'attendee', attendee.id)" class="flex items-start gap-3 px-4 py-4 sm:items-center md:px-6">
            <CPAvatar :title="attendee.name" :src="attendee.imageUrl" size="md" source="client-portal" class="shrink-0" />

            <div class="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex min-w-0 items-center gap-2">
                  <CPText
                    :id="createElementId(idPrefix, 'attendee-name', attendee.id)"
                    size="lg"
                    weight="semibold"
                    class="min-w-0 truncate text-body"
                  >
                    {{ displayName(attendee) }}
                  </CPText>
                  <CPTag
                    v-if="attendee.badge"
                    :id="createElementId(idPrefix, 'attendee-badge', attendee.id)"
                    size="xs"
                    :color="badgeColor(attendee.badge)"
                    :bordered="false"
                    :interactive="false"
                    class="shrink-0"
                  >
                    <CPText size="xs" weight="bold">{{ attendee.badge }}</CPText>
                  </CPTag>
                </div>
                <div v-if="attendee.email" class="mt-1 min-w-0 text-500">
                  <span class="flex min-w-0 items-center gap-1.5">
                    <Mail01Icon class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <CPText size="sm" weight="normal" class="min-w-0 truncate">{{ attendee.email }}</CPText>
                  </span>
                </div>
              </div>

              <div v-if="canShowChat(attendee) || canShowFollow(attendee)" class="flex shrink-0 gap-2">
                <CPButton
                  v-if="canShowChat(attendee)"
                  :id="createElementId(idPrefix, 'attendee-chat', attendee.id)"
                  type="secondary"
                  color="secondary"
                  size="xs"
                  class="event-detail-action-button"
                  @click="onChat(attendee)"
                >
                  <template #iconLeft>
                    <MessageChatCircleIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
                  </template>
                  {{ t('clientportal.events.detail.attendeesChat') }}
                </CPButton>
                <CPButton
                  v-if="canShowFollow(attendee)"
                  :id="createElementId(idPrefix, 'attendee-follow', attendee.id)"
                  :type="isFollowing(attendee.id) ? 'primary' : 'secondary'"
                  :color="isFollowing(attendee.id) ? 'primary' : 'secondary'"
                  size="xs"
                  class="event-detail-action-button"
                  @click="toggleFollow(attendee.id)"
                >
                  <template #iconLeft>
                    <UserPlus01Icon class="h-4 w-4 shrink-0" aria-hidden="true" />
                  </template>
                  {{
                    isFollowing(attendee.id)
                      ? t('clientportal.events.detail.attendeesFollowing')
                      : t('clientportal.events.detail.attendeesFollow')
                  }}
                </CPButton>
              </div>
            </div>
          </div>
          <CPDivider
            v-if="index < pageItems.length - 1"
            :id="createElementId(idPrefix, 'attendee-divider', attendee.id)"
            class-name="px-4"
            margin-top="0px"
            margin-bottom="0px"
          />
        </template>
      </div>

      <CPText v-if="pageItems.length === 0" size="md" weight="normal" class="px-4 pb-8 text-center text-500 md:px-6">
        {{ t('clientportal.events.detail.attendeesEmpty') }}
      </CPText>
    </EventDetailTabPanel>
  </div>
</template>

<style scoped>
  .event-detail-chip-button {
    --n-height: 2rem;
    --n-font-size: 0.8125rem;
    --n-padding: 0 0.75rem;
    --n-border-radius: 999px;
    font-weight: 600;
    white-space: nowrap;
  }

  .event-detail-action-button {
    --n-height: 2rem;
    --n-font-size: 0.8125rem;
    --n-padding: 0 0.75rem;
    --n-border-radius: 0.5rem;
    font-weight: 600;
  }
</style>
